import { ChevronDown, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { ValueChainGraph } from '@/types/valueChain';

type Crumb = { label: string; onClick?: () => void };

type Props = {
  graph: ValueChainGraph;
  graphs: ValueChainGraph[];
  crumbs: Crumb[];
  query: string;
  onQueryChange: (value: string) => void;
  onSubmitSearch: () => void;
  searchOpen: boolean;
  searchHits: Array<{ id: string; name: string; type: string }>;
  onPickSearch: (id: string) => void;
  onGraphChange: (id: string) => void;
  showGraphMenu: boolean;
  onShowGraphMenu: (open: boolean) => void;
  partCount: number;
  mappedRelationships: number;
  maxDepth: number;
  compact?: boolean;
  hideCrumbs?: boolean;
};

function formatCount(value: number): string {
  return value.toLocaleString();
}

export function ExplorerTopBar({
  graph,
  graphs,
  crumbs,
  query,
  onQueryChange,
  onSubmitSearch,
  searchOpen,
  searchHits,
  onPickSearch,
  onGraphChange,
  showGraphMenu,
  onShowGraphMenu,
  partCount,
  mappedRelationships,
  maxDepth,
  compact = false,
  hideCrumbs = false,
}: Props) {
  const navigate = useNavigate();

  return (
    <header className={`shrink-0 border-b border-gray-200 bg-white px-6 ${compact ? 'py-3' : 'py-4'}`}>
      <div className={`flex items-center gap-4 ${hideCrumbs ? 'justify-end' : 'justify-between'}`}>
        {hideCrumbs ? null : (
          <nav className="flex min-w-0 flex-wrap items-center gap-1 text-[12px] text-gray-400">
            {crumbs.map((crumb, index) => (
              <span key={`${crumb.label}-${index}`} className="flex items-center gap-1">
                {index > 0 ? <span>›</span> : null}
                {crumb.onClick ? (
                  <button type="button" onClick={crumb.onClick} className="hover:text-gray-700">
                    {crumb.label}
                  </button>
                ) : (
                  <span className={index === crumbs.length - 1 ? 'text-gray-700' : ''}>{crumb.label}</span>
                )}
              </span>
            ))}
            {compact ? null : (
              <span className="relative ml-3">
                <button
                  type="button"
                  onClick={() => onShowGraphMenu(!showGraphMenu)}
                  className="inline-flex items-center gap-1 text-[13px] font-medium text-gray-800"
                >
                  {graph.name}
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                </button>
                {showGraphMenu ? (
                  <div className="absolute top-7 left-0 z-20 w-64 rounded-xl border border-slate-200 bg-white py-1 shadow-sm">
                    {graphs.map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => onGraphChange(item.id)}
                        className={`block w-full px-3 py-2 text-left text-sm ${
                          item.id === graph.id
                            ? 'bg-gray-50 font-medium text-gray-900'
                            : 'text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        {item.name}
                      </button>
                    ))}
                  </div>
                ) : null}
              </span>
            )}
          </nav>
        )}

        <div className="flex shrink-0 items-center gap-3">
          <div className="relative w-[min(420px,40vw)]">
            <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              value={query}
              onChange={(event) => onQueryChange(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') onSubmitSearch();
              }}
              placeholder="Search for a company, product, supplier, material..."
              className="w-full rounded-full border border-gray-200 bg-gray-50 py-2 pr-4 pl-10 text-sm text-gray-800 outline-none placeholder:text-gray-400 focus:border-gray-300 focus:bg-white"
            />
            {searchOpen ? (
              <div className="absolute top-11 z-30 max-h-72 w-full overflow-y-auto rounded-xl border border-gray-200 bg-white py-1 shadow-sm">
                {searchHits.length === 0 ? (
                  <p className="px-3 py-2 text-sm text-gray-500">No matching nodes.</p>
                ) : (
                  searchHits.slice(0, 12).map((hit) => (
                    <button
                      key={hit.id}
                      type="button"
                      onClick={() => onPickSearch(hit.id)}
                      className="flex w-full items-center justify-between px-3 py-2 text-left hover:bg-gray-50"
                    >
                      <span className="truncate text-sm text-gray-900">{hit.name}</span>
                      <span className="ml-3 shrink-0 text-[11px] text-gray-400">{hit.type}</span>
                    </button>
                  ))
                )}
              </div>
            ) : null}
          </div>
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-700"
            aria-label="Back to dashboard"
          >
            LT
          </button>
        </div>
      </div>

      {compact ? null : (
      <div className="mt-3 flex flex-wrap items-end justify-between gap-x-8 gap-y-3">
        <div className="min-w-0">
          <div className="relative">
            <button
              type="button"
              onClick={() => onShowGraphMenu(!showGraphMenu)}
              className="inline-flex max-w-full items-center gap-2 text-left"
            >
              <h1 className="truncate text-[28px] leading-tight font-semibold tracking-tight text-gray-900">
                {graph.name}
              </h1>
              <ChevronDown className="h-5 w-5 shrink-0 text-gray-400" />
            </button>
            {showGraphMenu ? (
              <div className="absolute top-10 left-0 z-20 w-64 rounded-xl border border-gray-200 bg-white py-1 shadow-sm">
                {graphs.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => onGraphChange(item.id)}
                    className={`block w-full px-3 py-2 text-left text-sm ${
                      item.id === graph.id
                        ? 'bg-gray-50 font-medium text-gray-900'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {item.name}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
          <p className="mt-1 text-sm text-gray-500">
            Value chain, suppliers, materials, and economic impact
            {graph.status ? (
              <span className="ml-2 text-[11px] tracking-wide text-gray-400 uppercase">
                {graph.status}
              </span>
            ) : null}
          </p>
        </div>

        <dl className="flex flex-wrap items-end gap-8">
          <Stat value={formatCount(partCount)} label="Total nodes" />
          <Stat value={formatCount(mappedRelationships)} label="Mapped relationships" />
          <Stat value={String(maxDepth)} label="Depth explored" />
          <div className="text-right">
            <dd className="text-sm font-medium text-gray-900">
              {new Date().toLocaleDateString(undefined, {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}
            </dd>
            <dt className="text-[11px] text-gray-400">Last updated</dt>
          </div>
        </dl>
      </div>
      )}
    </header>
  );
}

function Stat({ value, label }: { value: string; label: string }) {
  return (
    <div className="text-right">
      <dd className="text-xl font-semibold tracking-tight text-gray-900">{value}</dd>
      <dt className="text-[11px] text-gray-400">{label}</dt>
    </div>
  );
}
